import React, { useState, useEffect } from 'react';
import { Box, Text, Button, HStack, Input, InputGroup, InputRightElement } from '@chakra-ui/react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import BackButton from '../components/BackButton';
import axios from 'axios';
import { useConfig } from '../contexts/ConfigContext';
import { useSupabase } from '../contexts/SupabaseContext';
import { useUser } from '../contexts/UserContext';
import { USER_TYPES, ROUTES, UserType, API_ROUTES } from '../constants';



const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { config } = useConfig();
  const { login: supabaseLogin } = useSupabase();
  const { login: setUser } = useUser();

  // Store return path when arriving at login page (from ProtectedRoute redirect)
  useEffect(() => {
      const state = location.state as { from?: { pathname: string } } | null;
      if (state?.from?.pathname) {
        // Store the return path in sessionStorage
        sessionStorage.setItem('authReturnPath', state.from.pathname);
      }
  }, [location]);

  // Handle URL parameters to determine mode and user type
  useEffect(() => {
    const mode = searchParams.get('mode');
    const type = searchParams.get('userType');

    if (mode === 'signup') {
      setIsSignupMode(true);
      if (type === USER_TYPES.BUSINESS || type === USER_TYPES.INFLUENCER) {
        setUserType(type as UserType);
      }
    } else {
      setIsSignupMode(false);
      setUserType(null);
    }
  }, [searchParams]);


  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if button should be enabled based on mode
  // For login mode, email and password validation is required
  // For signup mode, email validation is required
  const isFormValid = email && isValidEmail(email) && (!isSignupMode ? password : true) && !isLoading;

  const handleSubmit = async () => {
    // Clear any previous error messages
    setErrorMessage(null);

    try {
      if (isSignupMode) {
        // Check if account exists before navigating
        if (!userType) {
          setErrorMessage('Please select a user type');
          return;
        }

        if (!config?.apiUrl) {
          setErrorMessage('Configuration error. Please try again.');
          return;
        }

        setIsLoading(true);

        try {
          // Check if account exists
          const response = await axios.post(`${config.apiUrl}${API_ROUTES.AUTH_CHECK_ACCOUNT}`, {
            email,
            userType
          });

          if (response.data.exists) {
            setErrorMessage('Account exists, please login');
            setIsLoading(false);
            return;
          }

          // Account doesn't exist, proceed to signup wizard
          if (userType === USER_TYPES.INFLUENCER) {
            navigate(ROUTES.INFLUENCER_SIGNUP_WIZARD, { state: { email } });
          } else if (userType === USER_TYPES.BUSINESS) {
            navigate(ROUTES.BUSINESS_SIGNUP_WIZARD, { state: { email } });
          }
        } catch (error: any) {
          // If it's a network error or server error, show appropriate message
          if (error.response?.status === 400) {
            setErrorMessage('Invalid email or user type');
          } else {
            setErrorMessage('Failed to check account. Please try again.');
          }
        } finally {
          setIsLoading(false);
        }
      } else {
        // Login with Supabase
        if (!email || !password) {
          setErrorMessage('Please enter both email and password');
          return;
        }

        setIsLoading(true);
        try {
          // Clear any existing user data before starting new login
          sessionStorage.removeItem('user_data');
          
          // Ensure return path is stored before login
          const state = location.state as { from?: { pathname: string } } | null;
          if (state?.from?.pathname) {
            sessionStorage.setItem('authReturnPath', state.from.pathname);
          }
          
          // Login with Supabase
          await supabaseLogin(email, password);
          
          // Fetch user profile from backend after successful Supabase login
          if (!config?.apiUrl) {
            throw new Error('API URL not configured');
          }

          const storedReturnPath = sessionStorage.getItem('authReturnPath');
          const urlReturnPath = new URLSearchParams(location.search).get('returnTo');
          
          const initialReturnPath = storedReturnPath || state?.from?.pathname || urlReturnPath;
          const isAdminRoute = initialReturnPath === '/admin-users';
          const requestedUserType: 'admin' | null = isAdminRoute ? 'admin' : null;

          let userType: UserType | null = null;
          let userData: any = null;

          try {
            const profileResponse = await axios.get(`${config.apiUrl}${API_ROUTES.USER_PROFILE}`, {
              params: {
                userType: requestedUserType
              }
            });
            userData = profileResponse.data;
            userType = userData.userType || null;
          } catch (profileError: any) {
            if (profileError.response?.status === 403 || profileError.response?.status === 404) {
              console.warn('User profile not found. May be admin or unapproved user.');
              userType = null;
            } else {
              throw profileError;
            }
          }

          // Store user data in UserContext
          if (userData && userType) {
            let first_name = '';
            let last_name = '';
            
            if (userType === USER_TYPES.INFLUENCER) {
              first_name = userData.data?.firstName || userData.firstName || '';
              last_name = userData.data?.lastName || userData.lastName || '';
            } else if (userType === USER_TYPES.BUSINESS) {
              first_name = userData.data?.contactPerson?.firstName || userData.contactPerson?.firstName || '';
              last_name = userData.data?.contactPerson?.lastName || userData.contactPerson?.lastName || '';
            } else if (userType === 'admin') {
              // Admin users might not have first_name/last_name in the response
              first_name = userData.first_name || userData.firstName || '';
              last_name = userData.last_name || userData.lastName || '';
            }
            
            const userInfo = {
              id: userData.id,
              first_name: first_name,
              last_name: last_name,
              email: userData.email,
              userType: userType
            };
            setUser(userInfo);
          }

          // Determine return path
          let returnPath = null;
          if (userType === 'admin') {
            returnPath = '/admin-users';
          } else if (userType === USER_TYPES.INFLUENCER || userType === USER_TYPES.BUSINESS) {
            returnPath = userType === USER_TYPES.INFLUENCER ? ROUTES.INFLUENCER_HOME : ROUTES.BUSINESS_HOME;
          } else {
            returnPath = storedReturnPath || 
                        state?.from?.pathname || 
                        urlReturnPath ||
                        '/';
          }

          if (storedReturnPath) {
            sessionStorage.removeItem('authReturnPath');
          }

          // Navigate to appropriate route
          navigate(returnPath, { replace: true });
        } catch (error: any) {
          console.error('Login error:', error);
          setErrorMessage(error.message || 'Failed to login. Please check your credentials.');
        } finally {
          setIsLoading(false);
        }
      }
    } catch (error) {
      // Handle server errors
      if (isSignupMode) {
        setErrorMessage('Signup failed. Please try again.');
      } else {
        setErrorMessage('Invalid email or password');
      }
    }
  };


  const handleForgotPasswordClick = () => {
    navigate(ROUTES.FORGOT_PASSWORD);
  };

  const handleSignUpClick = () => {
    navigate(ROUTES.HOME);
  };

  return (
    <Box
      className="base-container"
    >
      {/* Main Content */}
      <Box className='from-content-container'>
        {/* Back Arrow Button - only show in signup mode */}
        {isSignupMode && (
          <Box className="back-buttom-box">
            <BackButton onClick={() => navigate('/')} />
          </Box>
        )}

        {/* Title - positioned at y: 124 */}
        <Box
          className="simple-box"
          top="124px"
          left={isSignupMode ? "73px" : "20px"}
        >
          <Text
            className=" title-text"
          >
            {isSignupMode ? 'Sign up with email' : 'Login with email'}
          </Text>
        </Box>

        {/* Error Message - positioned at y: 170 */}
        {errorMessage && (
          <Box
            position="absolute"
            top="170px"
            left="20px"
          >
            <Text
              className="error-message"
            >
              {errorMessage}
            </Text>
          </Box>
        )}

        {/* Email Input - positioned at y: 196 */}
        <Box
          className="simple-box"
          top="196px"
        >
          <InputGroup>
            <Input
              placeholder="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Clear error when user starts typing
                if (errorMessage) {
                  setErrorMessage(null);
                }
              }}
              className="input-field"
              isDisabled={isLoading}
              id="email"
              name="email"
            />
            <InputRightElement
              position="absolute"
              left="0"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
            >
              <svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.9725 2.66667C17.9725 1.75 17.2157 1 16.2907 1H2.83612C1.91112 1 1.1543 1.75 1.1543 2.66667M17.9725 2.66667V12.6667C17.9725 13.5833 17.2157 14.3333 16.2907 14.3333H2.83612C1.91112 14.3333 1.1543 13.5833 1.1543 12.6667V2.66667M17.9725 2.66667L9.56339 8.5L1.1543 2.66667" stroke="#90909A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </InputRightElement>
          </InputGroup>
        </Box>

        {/* Password Input - positioned at y: 246 - only show in login mode */}
        {!isSignupMode && (
          <Box
            className="simple-box"
            top="246px"
          >
            <InputGroup>
              <Input
                className="input-field"
                type={showPassword ? 'text' : 'password'}
                placeholder="**********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                id="password"
                name="password"
              />
              <Box
                className="icon-box"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5" y="11" width="14" height="10" stroke="#90909A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 8C7 5.23858 9.23858 3 12 3V3C14.7614 3 17 5.23858 17 8V11H7V8Z" stroke="#90909A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17Z" stroke="#90909A" strokeWidth="1.5"/>
                </svg>
              </Box>
              <Box
                className="icon-box"
                right="16px"
                left="auto"
                onClick={() => setShowPassword(!showPassword)}
                pointerEvents="auto"
                cursor="pointer"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {showPassword ? (
                    // Eye crossed icon
                    <>
                      <path d="M22 12C22 12 19 18 12 18C5 18 2 12 2 12C2 12 5 6 12 6C19 6 22 12 22 12Z" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="12" cy="12" r="3" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M3 21L20 4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
                    </>
                  ) : (
                    // Eye icon
                    <>
                      <path d="M22 12C22 12 19 18 12 18C5 18 2 12 2 12C2 12 5 6 12 6C19 6 22 12 22 12Z" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="12" cy="12" r="3" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
                    </>
                  )}
                </svg>
              </Box>
            </InputGroup>
          </Box>
        )}

        {/* Forgot Password - only show in login mode */}
        {!isSignupMode && (
          <Box
            className="simple-box"
            top="310px"
          >
            <Text
              className="regular-text"
              onClick={handleForgotPasswordClick}
              display="inline-block"
              width="fit-content"
              whiteSpace="nowrap"
            >
              Forgot Password?
            </Text>
          </Box>
        )}

        {/* Login/Signup Button - positioned at y: 399 in login mode, y: 266 in signup mode */}
        <Box
          className="button-big-container"
          top={isSignupMode ? "266px" : "366px"}
        >
          <Button
            onClick={handleSubmit}
            isDisabled={!isFormValid}
            isLoading={isLoading}
            loadingText={isSignupMode ? "Checking..." : "Redirecting..."}
            className="button-big"
          >
            {isSignupMode ? 'Sign up' : 'Login'}
          </Button>
        </Box>
        
        {/* Sign Up Link - positioned 30px from bottom */}
        <Box className="simple-box">
          <HStack justify="space-between">
            <Text
              className="static-text"
            >
              {isSignupMode ? 'Already have an account?' : "Don't have an account?"}
            </Text>
            <Text
              className="regular-text"
              textDecoration="underline"
              cursor="pointer"
              onClick={isSignupMode ? () => navigate(ROUTES.LOGIN) : handleSignUpClick}
            >
              {isSignupMode ? 'Login' : 'Sign Up'}
            </Text>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
